import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { FeatureToggle, isFeatureEnabled } from "./featureToggle";

/**
 * Get access token from local storage
 * Falls back to legacy key if okta storage is not present
 */
export const getAccessToken = () => {
  const tokenStorage = localStorage.getItem("okta-token-storage");
  return tokenStorage
    ? JSON.parse(tokenStorage)?.accessToken?.accessToken
    : localStorage.getItem("accessToken");
};

export const getUserGPID = () => {
  const tokenInfo = localStorage.getItem("okta-token-storage");
  const { accessToken } = JSON.parse(tokenInfo || "{}");
  const userInfo = accessToken?.claims || {};
  return userInfo?.gpid;
};

/**
 * Get program ID from global state or local storage
 */
export const getProgramId = (globalState = window?.globalState) => {
  try {
    return globalState?.programConfig?.id || localStorage.getItem("programId");
  } catch {
    return localStorage.getItem("programId");
  }
};

/**
 * Generate SHA-256 checksum of a File or Blob as hex string
 */
export async function generateSHA256(fileOrBlob) {
  const buffer = await fileOrBlob.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  return hashHex;
}

/**
 * Build FormData for file upload with JSON metadata
 * metadataFieldName defaults to "metadata" to match backend expectations
 */
export function buildMultipartFormData({
  file,
  metadata,
  metadataFieldName = "metadata",
}) {
  const formData = new FormData();
  formData.append("file", file);

  if (metadata !== undefined) {
    const metadataJSON =
      typeof metadata === "string" ? metadata : JSON.stringify(metadata);
    const metadataBlob = new Blob([metadataJSON], { type: "application/json" });
    formData.append(metadataFieldName, metadataBlob);
  }

  return formData;
}

/**
 * Upload a file with polling until processing completes
 *
 * Params:
 * - endpointBaseURI: base URL ending with '/files/' or equivalent, e.g. `${BASE}files/`
 * - context: server-side context string (e.g., INSTANT_WIN_IMPORT_PRIZE_SEED_DATA)
 * - file: File/Blob
 * - metadata: object or JSON string to send in 'metadata' field
 * - accessToken: optional, defaults to getAccessToken()
 * - programId: optional, will be added to headers if provided
 * - autoProcess: boolean -> auto_process=true/false query param
 * - forceUpload: boolean -> force-upload=true in query
 * - extraParams: record of additional query params
 * - importPath: default 'import'
 * - pollingPath: default 'polling'
 * - polling: boolean, default true. If false, returns immediately after upload without polling
 * - maxAttempts: default 30
 * - pollingIntervalMs: default 2000
 * - headersExtra: extra headers to merge
 */
export async function uploadWithPolling({
  endpointBaseURI,
  context,
  file,
  metadata,
  accessToken = getAccessToken(),
  programId = getProgramId(),
  autoProcess = true,
  forceUpload = false,
  extraParams = {},
  importPath = "import",
  pollingPath = "polling",
  polling = true,
  maxAttempts = 30,
  pollingIntervalMs = 2000,
  headersExtra = {},
  metadataFieldName = "metadata",
  importFor = "",
}) {
  if (!endpointBaseURI) throw new Error("endpointBaseURI is required");
  if (!context) throw new Error("context is required");
  if (!file) throw new Error("file is required");

  const fileSha256 = await generateSHA256(file);

  const params = new URLSearchParams({
    context,
    auto_process: String(Boolean(autoProcess)),
    file_sha256: fileSha256,
    ...extraParams,
  });

  if (forceUpload) params.set("force-upload", "true");

  const formData = buildMultipartFormData({ file, metadata, metadataFieldName });

  const endpoint = `${endpointBaseURI}${importPath}?${params.toString()}`;

  const headers = {
    Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
    ...(programId ? { "program-id": programId } : {}),
    "Content-Type": "multipart/form-data",
    ...headersExtra,
  };

  // Remove undefined headers
  Object.keys(headers).forEach((k) => headers[k] === undefined && delete headers[k]);

  const response = await axios.post(endpoint, formData, { headers });

  if (response.status !== 202 && response.status !== 201) {
    throw new Error("Upload failed");
  }

  // If polling is disabled, return immediately with response data
  if (!polling) {
    return {
      status: response.status,
      response: response,
      polling_id: response.data?.polling_id || response.data?.pollind_id,
    };
  }

  let pollingId = response.data?.polling_id || response.data?.pollind_id;
  if (!pollingId) {
    // Some flows complete synchronously
    return {
      status: 202,
      ready_to_download: true,
      ...((importFor === "sweepstakes" ||
        importFor === "platformSegments" ||
        importFor === "instantWin") && {
        status: "PROCESSED",
      }),
    };
  }

  let attempts = 0;
  while (attempts < maxAttempts) {
    attempts += 1;
    await new Promise((r) => setTimeout(r, pollingIntervalMs));

    const statusUrl = `${endpointBaseURI}${pollingPath}/${pollingId}`;
    const statusRes = await axios.get(statusUrl, {
      headers: {
        ...(headers.Authorization ? { Authorization: headers.Authorization } : {}),
        ...(programId ? { "program-id": programId } : {}),
        ...headersExtra,
      },
    });

    const statusData = statusRes.data;

    if (statusData.status === "FAILED") {
      const errorMessage =
        statusData?.sanitized_message ||
        statusData?.error_message?.message ||
        "Upload failed";
      throw new Error(errorMessage);
    }

    if (
      importFor === "sweepstakes" ||
      importFor === "platformSegments" ||
      importFor === "instantWin"
    ) {
      if (statusData.status === "PROCESSED" || statusData.status === "COMPLETED") {
        return { status: "PROCESSED", file_id: statusData.file_id };
      }
    } else if (statusData.ready_to_download) {
      return { status: 202, file_id: statusData.file_id, ready_to_download: true };
    }
  }

  throw new Error("Upload processing timed out");
}

/**
 * Initiate an export job and poll until file is ready, then trigger download
 *
 * Params:
 * - endpointBaseURI: base URL ending with '/files/' or service root that supports 'export', 'polling', 'download'
 * - context: string
 * - parameters: object payload for export request
 * - exportType: e.g. 'text/csv'
 * - filename: download filename suggestion
 * - accessToken, programId: optional
 * - polling: boolean, default true. If false, returns immediately after export initiation without polling
 * - maxAttempts, pollingIntervalMs: optional
 * - headersExtra: extra headers to merge
 */
export async function exportWithPolling({
  endpointBaseURI,
  context,
  parameters,
  exportType = "text/csv",
  filename = `export-${Date.now()}.csv`,
  accessToken = getAccessToken(),
  programId = getProgramId(),
  maxAttempts = 30,
  pollingIntervalMs = 2000,
  headersExtra = {},
  fileType = "CSV",
}) {
  // if (!endpointBaseURI) throw new Error("endpointBaseURI is required");
  if (!context) throw new Error("context is required");
  const useLegacyExportImport = isFeatureEnabled(
    FeatureToggle.useLegacyExportImport
  );
  const exportPath = "files/";

  let defaultBaseUrl = window?.REWARD_PARTNER_GROUP_FILE_MANAGER_BASE_URI;

  let baseUrl = window?.REWARD_PARTNER_GROUP_FILE_MANAGER_BASE_URI?.replace(
    "files/",
    exportPath
  );

  let polling = useLegacyExportImport ? true : false;

  if (endpointBaseURI) baseUrl = endpointBaseURI;

  const userId = getUserGPID();
  if (!userId) throw new Error("User Id is required");

  const exportEndpoint = `${baseUrl}export`;

  const headers = {
    Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
    ...(programId ? { "program-id": programId } : {}),
    "Content-Type": "application/json",
    ...headersExtra,
  };
  Object.keys(headers).forEach((k) => headers[k] === undefined && delete headers[k]);

  const exportRes = await axios.post(
    exportEndpoint,
    {
      context,
      parameters,
      export_type: exportType,
      content_type: exportType,
      operation_type: "EXPORT",
      file_type: fileType,
      program_id: programId,
      user_id: userId,
      entity_id: uuidv4(),
      name: filename,
      file_name: filename,
    },
    { headers }
  );

  if (exportRes.status !== 202 && exportRes.status !== 201) {
    throw new Error("Failed to initiate export");
  }

  // If polling is disabled, return immediately with response data
  if (!polling) {
    return {
      status: 200,
      response: exportRes,
      polling_id: exportRes.data?.polling_id,
      success: true,
      message: "Export initiated successfully",
      description: "View details in file history.",
    };
  }

  let pollingId = exportRes.data?.polling_id || exportRes.data?.pollind_id;
  if (!pollingId) throw new Error("Export did not return a polling id");

  let attempts = 0;
  let fileId = null;

  while (attempts < maxAttempts) {
    attempts += 1;
    await new Promise((r) => setTimeout(r, pollingIntervalMs));

    const statusUrl = `${defaultBaseUrl}polling/${pollingId}`;
    const statusRes = await axios.get(statusUrl, { headers: { ...headers } });
    const statusData = statusRes.data;

    if (statusData.ready_to_download) {
      fileId = statusData.file_id;
      break;
    }
  }

  if (!fileId) throw new Error("Export timed out or failed");

  const downloadUrl = `${defaultBaseUrl}download/${fileId}`;
  const fileRes = await axios.get(downloadUrl, {
    headers: { ...headers },
    responseType: "blob",
  });

  const url = window.URL.createObjectURL(new Blob([fileRes.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return {
    status: 200,
    file_id: fileId,
    success: true,
    message: "Export completed successfully",
    description: "File downloaded successfully",
  };
}

export const formatFileTransferFilterValue = (obj) => {
  const entries = Object.entries(obj)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        const arrayValues = value
          .filter((v) => v !== null && v !== undefined)
          .map((v) => `\\"${v}\\"`)
          .join(", ");

        if (!arrayValues) return null;

        return `${key}: [${arrayValues}]`;
      }
      if (typeof value === "string") {
        return `${key}: \\"${value}\\"`;
      }
      return `${key}: ${value}`;
    })
    .filter(Boolean);

  return entries.length ? `{${entries.join(", ")}}` : "{}";
};
